import React from 'react';
import './UserSummaryStats.css';

// 발전량 단위 자동 변환 함수 (kW 기준, 소수점 앞 3자리까지 표시 후 다음 단위로 변환)
const formatPowerGeneration = (valueInKw) => {
  if (!valueInKw || valueInKw === 0) {
    return {
      value: '0',
      unit: 'kW'
    };
  }
  
  // 정수 부분의 자리수 계산 (소수점 제외)
  const integerPart = Math.floor(Math.abs(valueInKw));
  const digitCount = integerPart.toString().length;
  
  // 6자리수(999,999) 이하 → kW로 표시 (천 단위 구분자 포함)
  if (digitCount <= 6) {
    return {
      value: Math.round(valueInKw).toLocaleString(),
      unit: 'kW'
    };
  }
  
  // 7자리 이상 → 다음 단위로 변환 (소수점 3자리까지)
  // 변환된 값의 정수 부분이 3자리를 넘으면 다음 단위로
  if (digitCount > 15) {
    // 16자리 이상 → PW (페타와트)
    const pwValue = valueInKw / 1000000000000000;
    const pwIntegerPart = Math.floor(Math.abs(pwValue));
    if (pwIntegerPart.toString().length > 3) {
      // PW도 3자리 넘으면 다음 단위로 (실제로는 없지만 확장성)
      return {
        value: pwValue.toFixed(3),
        unit: 'PW'
      };
    }
    return {
      value: pwValue.toFixed(3),
      unit: 'PW'
    };
  } else if (digitCount > 12) {
    // 13자리 이상 → TW (테라와트)
    const twValue = valueInKw / 1000000000000;
    const twIntegerPart = Math.floor(Math.abs(twValue));
    if (twIntegerPart.toString().length > 3) {
      return formatPowerGeneration(valueInKw); // 재귀 호출로 다음 단위로
    }
    return {
      value: twValue.toFixed(3),
      unit: 'TW'
    };
  } else if (digitCount > 9) {
    // 10자리 이상 → GW (기가와트)
    const gwValue = valueInKw / 1000000000;
    const gwIntegerPart = Math.floor(Math.abs(gwValue));
    if (gwIntegerPart.toString().length > 3) {
      return formatPowerGeneration(valueInKw); // 재귀 호출로 다음 단위로
    }
    return {
      value: gwValue.toFixed(3),
      unit: 'GW'
    };
  } else {
    // 7-9자리 → MW (메가와트)
    const mwValue = valueInKw / 1000000;
    const mwIntegerPart = Math.floor(Math.abs(mwValue));
    if (mwIntegerPart.toString().length > 3) {
      return formatPowerGeneration(valueInKw); // 재귀 호출로 다음 단위로
    }
    return {
      value: mwValue.toFixed(3),
      unit: 'MW'
    };
  }
};

const UserSummaryStats = ({ powerLocations, user }) => {
  // 통계 계산
  const totalPowerLocations = powerLocations.length;
  const normalPowerLocations = powerLocations.filter(p => p.status === 'normal').length;
  // const errorPowerLocations = powerLocations.filter(p => p.status !== 'normal').length;
  const totalPowerGeneration = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.powerGeneration, 0);
  
  // MWh를 kWh로 변환 (1 MWh = 1,000 kWh = 1,000 kW)
  const totalDailyGenerationKw = powerLocations.reduce((sum, powerLocation) => sum + ((powerLocation.dailyGeneration || 0) * 1000), 0);
  const totalMonthlyGenerationKw = powerLocations.reduce((sum, powerLocation) => sum + ((powerLocation.monthlyGeneration || 0) * 1000), 0);
  const totalTotalGenerationKw = powerLocations.reduce((sum, powerLocation) => sum + ((powerLocation.totalGeneration || 0) * 1000), 0);
  
  // 발전량 단위 변환
  const dailyGenerationFormatted = formatPowerGeneration(totalDailyGenerationKw);
  const monthlyGenerationFormatted = formatPowerGeneration(totalMonthlyGenerationKw);
  const totalGenerationFormatted = formatPowerGeneration(totalTotalGenerationKw);

  const workingInverters = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.inverter.working, 0);
  const totalInverters = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.inverter.total, 0);
  const workingEnvironmentSensors = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.environmentSensor.working, 0);
  const totalEnvironmentSensors = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.environmentSensor.total, 0);
  const workingConnectionBoxes = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.connectionBox.working, 0);
  const totalConnectionBoxes = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.connectionBox.total, 0);
  const workingCommunications = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.communication.working, 0);
  const totalCommunications = powerLocations.reduce((sum, powerLocation) => sum + powerLocation.communication.total, 0);

  const stats = [
    {
      title: '총 발전소',
      value: totalPowerLocations,
      unit: '개',
      icon: '🏭',
      color: '#667eea'
    },
    {
      title: '정상 운영',
      value: normalPowerLocations,
      unit: '개',
      icon: '✅',
      color: '#10B981'
    },
    {
      title: '현재 발전량',
      value: totalPowerGeneration.toFixed(2),
      unit: 'kW',
      icon: '⚡',
      color: '#F59E0B'
    },
    {
      title: '오늘 발전량',
      value: dailyGenerationFormatted.value,
      unit: dailyGenerationFormatted.unit,
      icon: '📊',
      color: '#8B5CF6'
    },
    {
      title: '이번 달 발전량',
      value: monthlyGenerationFormatted.value,
      unit: monthlyGenerationFormatted.unit,
      icon: '📈',
      color: '#EF4444'
    },
    {
      title: '총 누적 발전량',
      value: totalGenerationFormatted.value,
      unit: totalGenerationFormatted.unit,
      icon: '🏆',
      color: '#06B6D4'
    }
  ];

  const equipmentStats = [
    {
      title: '인버터',
      working: workingInverters,
      total: totalInverters,
      percentage: totalInverters > 0 ? Math.round((workingInverters / totalInverters) * 100) : 0
    },
    {
      title: '환경센서',
      working: workingEnvironmentSensors,
      total: totalEnvironmentSensors,
      percentage: totalEnvironmentSensors > 0 ? Math.round((workingEnvironmentSensors / totalEnvironmentSensors) * 100) : 0
    },
    {
      title: '접속반',
      working: workingConnectionBoxes,
      total: totalConnectionBoxes,
      percentage: totalConnectionBoxes > 0 ? Math.round((workingConnectionBoxes / totalConnectionBoxes) * 100) : 0
    },
    {
      title: '통신',
      working: workingCommunications,
      total: totalCommunications,
      percentage: totalCommunications > 0 ? Math.round((workingCommunications / totalCommunications) * 100) : 0
    }
  ];

  // user가 없으면 아무것도 렌더링하지 않음
  if (!user) {
    return null;
  }

  return (
    <div className="user-summary-stats">
      <div className="stats-header">
        <h3>내 발전소 현황</h3>
      </div>

      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon" style={{ color: stat.color }}>
              {stat.icon}
            </div>
            <div className="stat-content">
              <div className="stat-title">{stat.title}</div>
              <div className="stat-value">
                {typeof stat.value === 'number' && stat.unit === '개' 
                  ? stat.value.toLocaleString() 
                  : stat.value}
                <span className="stat-unit">{stat.unit}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="equipment-status">
        <h4>장비 상태</h4>
        <div className="equipment-list">
          {equipmentStats.map((equipment, index) => (
            <div key={index} className="equipment-item">
              <div className="equipment-header">
                <span className="equipment-name">{equipment.title}</span>
                <span className="equipment-count">
                  {equipment.working}/{equipment.total}
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${equipment.percentage}%`,
                    backgroundColor: equipment.percentage >= 80 ? '#10B981' : 
                                   equipment.percentage >= 60 ? '#F59E0B' : '#EF4444'
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default UserSummaryStats;
